import { useMemo } from 'react';
import Checkbox from '../../../../atoms/Checkbox';
import MultipleSelect, { IMultipleOptions } from '../../../../atoms/MultipleSelect';
import { Typography } from '../../../../molecules/Typography';
import { usePrintComponentRequestStore } from '../../store/usePrintComponentRequest';
import { listExercisesYear } from '../../../../../../modules/Disciplines/utils/exerciseHelper';
import { useFetchSubareaInstitutions } from '../../../../../hooks/useFetchSubareaInstitutions';
import { useNewSubjectStore } from '../../../../../../modules/Disciplines/stores/newSubjectStore';

interface IAdicionalFilters {
  disabled: boolean;
  subject: string;
  slug?: string;
}

const AditionalFilters: React.FC<IAdicionalFilters> = ({
  disabled,
  subject,
  slug,
}) => {
  const {
    setDifficulty,
    setFavorites,
    setUnanswered,
    setRightAnswer,
    setWrongAnswer,
    setQuestionOptions,
    setExerciseTypes,
    setSelectedYear,
    setInstitutionType,
    exerciseTypes,
    difficulty,
    favorites,
    unanswered,
    rightAnswer,
    wrongAnswer,
    questionOptions,
    selectedYear,
    institutionType,
  } = usePrintComponentRequestStore();

  const { selectedSubjects } = useNewSubjectStore();

  const yearOptionsRaw = listExercisesYear();
  const yearOptions: IMultipleOptions[] = useMemo(() =>
    yearOptionsRaw
      .filter((opt) => opt.value !== 'all')
      .map((opt) => ({ value: String(opt.value), label: opt.label })),
    [yearOptionsRaw]
  );

  // Converte exerciseTypes para exerciseType compatível com o endpoint
  const exerciseType = useMemo(() => {
    const hasEnem = exerciseTypes.includes('enem');
    const hasVestibulares = exerciseTypes.includes('vest');

    if (hasEnem && !hasVestibulares) return 'enem';
    if (hasVestibulares && !hasEnem) return 'vest';
    return null;
  }, [exerciseTypes]);

  const fetchInstitutions = useFetchSubareaInstitutions({
    slug,
    selectedYear,
    exerciseType,
    difficulty,
    questionOptions,
    favorite: favorites,
    unanswered,
    rightAnswer,
    wrongAnswer,
    selectedSubjects,
    enabled: exerciseTypes.includes('vest'),
  });

  const institutionsOptions = useMemo<IMultipleOptions[]>(() => {
    const institutions = fetchInstitutions.data ?? [];
    return institutions.map((inst: { value: string | number; label: string }) => ({
      value: String(inst.value),
      label: inst.label,
    }));
  }, [fetchInstitutions.data]);

  const difficultyHandler = (level: string) => {
    if (difficulty.indexOf(level) >= 0) {
      const array = [...difficulty];

      array.splice(difficulty.indexOf(level), 1);
      setDifficulty(array);
    } else {
      const array = [...difficulty];
      array.push(level);

      setDifficulty(array);
    }
  };

  const questionHandler = (type: string) => {
    if (questionOptions.indexOf(type) >= 0) {
      const array = [...questionOptions];

      array.splice(questionOptions.indexOf(type), 1);
      setQuestionOptions(array);
    } else {
      const array = [...questionOptions];
      array.push(type);

      setQuestionOptions(array);
    }
  };

  const exerciseTypesHandler = (type: string) => {
    if (exerciseTypes.indexOf(type) >= 0) {
      const array = [...exerciseTypes];

      array.splice(exerciseTypes.indexOf(type), 1);
      if (!array.includes('vest')) {
        setInstitutionType([]);
      }
      setExerciseTypes(array);
    } else {
      const array = [...exerciseTypes];
      array.push(type);

      setExerciseTypes(array);
    }
  };

  const onChangeYear = (values: string[]) => {
    setSelectedYear(values);
    setInstitutionType([]);
  };

  return (
    <div className='flex flex-col gap-xl'>
      <Typography weight='bold'>
        Utilize os filtros para definir quais exercícios sobre o assunto{' '}
        {subject} você irá imprimir:
      </Typography>

      <div className={'flex flex-col gap-lg'}>
        <Typography weight='bold' size='small' className='mb-1'>
          FILTROS
        </Typography>
        <div className='flex justify-between gap-xl rounded-md bg-color-lighter xs:flex-col md:flex-row'>
          <div className='flex flex-1 flex-col gap-xl p-4'>
            <Checkbox
              onChange={() => difficultyHandler('easy')}
              label='Exercícios fáceis'
              checked={difficulty.indexOf('easy') >= 0}
              id='print-difficulty-easy'
              value='easy'
              variant='rounded'
              disabled={disabled}
            />
            <Checkbox
              onChange={() => difficultyHandler('medium')}
              label='Exercícios médios'
              checked={difficulty.indexOf('medium') >= 0}
              id='print-difficulty-medium'
              value='medium'
              variant='rounded'
              disabled={disabled}
            />
            <Checkbox
              onChange={() => difficultyHandler('hard')}
              label='Exercícios difíceis'
              checked={difficulty.indexOf('hard') >= 0}
              id='print-difficulty-hard'
              value='hard'
              variant='rounded'
              disabled={disabled}
            />
            <Checkbox
              onChange={() => difficultyHandler('random')}
              label='Aleatório'
              checked={difficulty.indexOf('random') >= 0}
              id='print-difficulty-random'
              value='random'
              variant='rounded'
              disabled={disabled}
            />
          </div>
          <div className='flex flex-1 flex-col gap-xl p-4'>
            <Checkbox
              onChange={() => questionHandler('single_answer')}
              label='Resposta única'
              checked={questionOptions.indexOf('single_answer') >= 0}
              id='print-type-single-answer'
              value='single_answer'
              variant='rounded'
              disabled={disabled}
            />
            <Checkbox
              onChange={() => questionHandler('multiple_answer')}
              label='Resposta somatória'
              checked={questionOptions.indexOf('multiple_answer') >= 0}
              id='print-type-multiple-answer'
              value='multiple_answer'
              variant='rounded'
              disabled={disabled}
            />
            <Checkbox
              onChange={() => questionHandler('discursive')}
              label='Resposta discursiva'
              checked={questionOptions.indexOf('discursive') >= 0}
              id='print-type-discursive'
              value='discursive'
              variant='rounded'
              disabled={disabled}
            />
          </div>
          <div className='flex flex-1 flex-col gap-xl p-4'>
            <Checkbox
              onChange={() => setFavorites(!favorites)}
              label='Exercícios favoritados'
              checked={favorites}
              id='print-favorites'
              value='favorites'
              variant='rounded'
              disabled={disabled}
            />
            <Checkbox
              onChange={() => setUnanswered(!unanswered)}
              label='Não respondidos'
              checked={unanswered}
              id='print-unanswered'
              value='unanswered'
              variant='rounded'
              disabled={disabled}
            />
            <Checkbox
              onChange={() => setWrongAnswer(!wrongAnswer)}
              label='Respostas erradas'
              checked={wrongAnswer}
              id='print-wrong-answer'
              value='wrongAnswer'
              variant='rounded'
              disabled={disabled}
            />
            <Checkbox
              onChange={() => setRightAnswer(!rightAnswer)}
              label='Respostas certas'
              checked={rightAnswer}
              id='print-right-answer'
              value='rightAnswer'
              variant='rounded'
              disabled={disabled}
            />
          </div>
        </div>

        <div className='flex justify-between gap-xl rounded-md bg-color-lighter xs:flex-col md:flex-row'>
          <div className='flex flex-1 gap-xl p-4'>
            <Checkbox
              onChange={() => exerciseTypesHandler('enem')}
              label='Enem'
              checked={exerciseTypes.indexOf('enem') >= 0}
              id='print-type-enem'
              value='enem'
              variant='rounded'
              disabled={disabled}
            />
          </div>
          <div className='flex flex-1 gap-xl p-4'>
            <Checkbox
              onChange={() => exerciseTypesHandler('vest')}
              label='Vestibulares'
              checked={exerciseTypes.indexOf('vest') >= 0}
              id='print-type-vest'
              value='vest'
              variant='rounded'
              disabled={disabled}
            />
          </div>
          <div className='flex flex-1 gap-xl p-4'></div>
        </div>

        <div className='flex justify-between gap-xl rounded-md bg-color-lighter xs:flex-col md:flex-row'>
          <div className='flex flex-1 flex-col gap-xl p-4'>
            <MultipleSelect
              options={yearOptions}
              label='Ano'
              placeholder='Todos os anos'
              onChange={onChangeYear}
              selected={selectedYear}
              className="w-full"
              disabled={disabled}
            />
          </div>
          {exerciseTypes.includes('vest') && (
            <div className='flex flex-1 flex-col gap-xl p-4'>
              <MultipleSelect
                options={institutionsOptions}
                label='Instituição'
                placeholder='Todas as instituições'
                onChange={(values) => setInstitutionType(values)}
                selected={institutionType}
                className="w-full"
                disabled={disabled || fetchInstitutions.isLoading}
              />
            </div>
          )}
          {!exerciseTypes.includes('vest') && (
            <div className='flex flex-1 gap-xl p-4'></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AditionalFilters;
